import { useCallback, useEffect, useRef, useState } from "react";
import { record } from "rrweb";
import * as signalR from '@microsoft/signalr';
import { API_ROUTES, SOCKET_API } from '@/constant/routes';

type UseLiveStreamingOptions = {
  maskAllInputs?: boolean; // default: true
  autoStart?: boolean; // default: true
};

export function useLiveStreaming({
                                   maskAllInputs = true,
                                   autoStart = false,
                                 }: UseLiveStreamingOptions = {}) {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const stopRecordingRef = useRef<null | (() => void)>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [lastOperatorId, setLastOperatorId] = useState<string | null>(null);

  const stop = useCallback(async () => {
    stopRecordingRef.current?.();
    stopRecordingRef.current = null;

    const conn = connectionRef.current;
    connectionRef.current = null;

    try {
      if (conn) await conn.stop();
    } catch (e) {
      console.error("[USER] stop connection error:", e);
    } finally {
      setIsConnected(false);
    }
  }, []);

  const startRecording = useCallback(
    (operatorId: string) => {
      const conn = connectionRef.current;
      if (!conn) return;

      // stop previous recording (if any)
      stopRecordingRef.current?.();
      stopRecordingRef.current = null;

      stopRecordingRef.current =
        record({
          emit(event:any) {
            if (conn.state === signalR.HubConnectionState.Connected) {
              conn.invoke("SendPlayerEvent", operatorId, event).catch((err:any) =>
                console.error("[USER] Send error:", err)
              );
            }
          },
          maskAllInputs,
        }) ?? null;

      console.log("[USER] Recording (re)started for:", operatorId);
    },
    [maskAllInputs]
  );

  const start = useCallback(async () => {
    // prevent duplicate starts
    if (connectionRef.current) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error("[USER] No token found in localStorage");
      return;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${SOCKET_API}${API_ROUTES.LIVE_STREAMING}`, { accessTokenFactory: () => token, withCredentials: false })
      .withAutomaticReconnect()
      .build();

    // handlers
    connection.on("ExecuteClick", (selector: string) => {
      console.log("[USER] ExecuteClick received:", selector);
      const element = document.querySelector(selector) as HTMLElement | null;
      if (element) element.click();
      else console.warn("[USER] Element not found:", selector);
    });

    connection.on("RestartRecording", (operatorId: string) => {
      console.log("[USER] Operator joined, restarting recording for:", operatorId);
      setLastOperatorId(operatorId);
      startRecording(operatorId);
    });

    connection.on("OperatorJoined", (operatorId: string) => {
      console.log("[USER] Operator is watching:", operatorId);
      setLastOperatorId(operatorId);
    });

    connection.onclose(() => {
      setIsConnected(false);
    });
    connection.onreconnected(() => {
      setIsConnected(true);
      // اگر خواستید بعد از reconnect ضبط را دوباره شروع کنید:
      // if (lastOperatorId) startRecording(lastOperatorId);
    });

    try {
      await connection.start();
      console.log("[USER] Connected to SignalR");
      setIsConnected(true);

      await connection.invoke("RegisterUser");
      console.log("[USER] Registered as user");
    } catch (err:any) {
      console.error("[USER] Connection failed:", err);
      try {
        await connection.stop();
      } catch {}
      return;
    }

    connectionRef.current = connection;
  }, [ startRecording]);

  // auto start/cleanup
  useEffect(() => {
    if (!autoStart) return;

    start();
    return () => {
      // cleanup on unmount
      stop();
    };
  }, [autoStart, start, stop]);

  return {
    start, // manually start if autoStart=false
    stop, // manually stop
    isConnected,
    lastOperatorId,
  };
}
